<script setup lang="ts">
import { toRefs, ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import { VCombobox, VIcon } from 'vuetify/components'
import searchIcon from '@/assets/icons/grey/search.svg'
import { Item } from '@/types/corporative'

const props = withDefaults(
  defineProps<{
    id?: string
    label: string
    required?: boolean
    items: Item[] | null
    modelValue?: string | Item | Item[]
    labelField?: keyof Item | string
    valueField?: keyof Item | string
    loading?: boolean
    error?: boolean
    placeholder?: string
    multiple?: boolean
    disabled?: boolean
    optionsId?: string
    description?: string
  }>(),
  {
    id: '',
    required: false,
    labelField: 'name',
    valueField: 'id',
    modelValue: '',
    loading: false,
    error: false,
    placeholder: '',
    multiple: false,
    disabled: false,
    optionsId: undefined,
    description: undefined,
  }
)

const emit = defineEmits([
  'update:modelValue',
  'select',
  'submit',
  'switch-modal',
  'search',
])

const { items, labelField, valueField, multiple } = toRefs(props)

const comboboxRef = ref<ComponentPublicInstance | null>(null)
const menu = ref(false)
const menuWidth = ref<number>(0)

let ro: ResizeObserver | null = null

const updateMenuWidth = () => {
  const el = (comboboxRef.value?.$el as HTMLElement | undefined) ?? null
  if (!el) return
  menuWidth.value = Math.round(el.getBoundingClientRect().width)
}

onMounted(() => {
  updateMenuWidth()

  const el = (comboboxRef.value?.$el as HTMLElement | undefined) ?? null
  if (!el) return

  ro = new ResizeObserver(() => updateMenuWidth())
  ro.observe(el)
})

onBeforeUnmount(() => {
  ro?.disconnect()
  ro = null
})

const displayTitle = (item: unknown): string => {
  const typedItem = item as Item
  return String(typedItem?.[labelField.value as keyof Item] ?? item)
}

const displayValue = (item: unknown): unknown => {
  const typedItem = item as Item
  return typedItem?.[valueField.value as keyof Item] ?? item
}

// quando abrir o menu, mede de novo (garante width certo)
const handleMenuUpdate = async (isOpen: boolean) => {
  menu.value = isOpen
  if (isOpen) {
    await nextTick()
    updateMenuWidth()
  }
}
</script>

<template>
  <VCombobox
    :id="id"
    ref="comboboxRef"
    persistent-placeholder
    :items="items ?? []"
    :label="label"
    :placeholder="placeholder"
    :multiple="multiple"
    variant="outlined"
    :chips="multiple"
    clearable
    :disabled="disabled"
    :loading="loading"
    :error="error"
    :item-title="displayTitle"
    :item-value="displayValue"
    :model-value="modelValue"
    :hint="props.description"
    :persistent-hint="!!props.description"
    :messages="props.description ? [props.description] : undefined"
    :hide-details="props.description || error ? false : 'auto'"
    class="w-100"
    attach="body"
    location-strategy="connected"
    :menu="menu"
    :menu-props="{
      contentClass: 'combo-menu',
      maxHeight: 280,
      zIndex: 4000,
      // 👇 AQUI é o pulo do gato: força width inline no content do overlay
      contentProps: {
        style: {
          width: menuWidth ? `${menuWidth}px` : undefined,
          minWidth: menuWidth ? `${menuWidth}px` : undefined,
          maxWidth: menuWidth ? `${menuWidth}px` : undefined,
        },
      },
    }"
    @update:menu="handleMenuUpdate"
    @update:model-value="
      (val: unknown) => {
        emit('update:modelValue', val)
        if (typeof val === 'object' && val !== null) {
          emit('select', val)
        }
      }
    "
    @keydown.enter="emit('submit')"
    @update:search="(val: string) => emit('search', val)"
  >
    <template #prepend-inner>
      <VIcon size="16">
        <img :src="searchIcon" alt="search" style="height: 16px; width: 16px" />
      </VIcon>
    </template>

    <template #append-item>
      <slot />
    </template>

    <template #no-data>
      <slot name="no-data">
        <div class="text-body-2 text-grey-darken-1" style="padding: 8px">
          {{ $t('general.noResults') }}
        </div>
      </slot>
    </template>
  </VCombobox>
</template>

<style>
/* MENU (global) */
.v-overlay__content.combo-menu {
  box-sizing: border-box;
  overflow: hidden;
}

.v-overlay__content.combo-menu .v-list {
  max-height: 280px;
  overflow-y: auto;
  max-width: 100%;
}

.v-overlay__content.combo-menu .v-list-item,
.v-overlay__content.combo-menu .v-list-item__content {
  min-width: 0;
  max-width: 100%;
}

.v-overlay__content.combo-menu .v-list-item-title {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
}
</style>
<style scoped>
/* CAMPO (scoped) */
:deep(.v-combobox .v-field) {
  min-height: 56px !important;
  height: 56px !important;

  /* espaço suficiente pro label outlined não cortar */
  --v-field-padding-top: 10px;
  --v-input-padding-top: 8px;
}

/* alinha o input verticalmente */
:deep(.v-combobox .v-field__input) {
  min-height: 56px !important;
  align-items: center;
}

/* NÃO mexe em top do label (isso costuma dar corte em outros estados) */
/* Remova seu:
:deep(.v-combobox .v-field-label) { top: 2px; }
*/
</style>
